import {debounce} from '@github/mini-throttle'
import {announce} from '@github-ui/aria-live'
import {PropertiesBreadcrumbsPageHeader} from '@github-ui/custom-properties-core/components/PropertiesBreadcrumbsPageHeader'
import {PropertiesPageTabs} from '@github-ui/custom-properties-core/components/PropertiesPageTabs'
import {TargetsList} from '@github-ui/custom-properties-core/components/TargetsList'
import {useBannerContext} from '@github-ui/custom-properties-core/context/BannerContext'
import type {OrgCustomPropertiesSetValuesPagePayload, Repository} from '@github-ui/custom-properties-types'
import {hpcAttrs} from '@github-ui/hpc'
import {listOrgReposPropertyValuesPath, repoOverviewUrl, updateRepoPropertyValuesFromOrgPath} from '@github-ui/paths'
import {useRoutePayload} from '@github-ui/react-core/use-route-payload'
import {ReposFilter} from '@github-ui/repos-filter'
import {getNoRepoMessage, NoReposMessage, type UserInfo} from '@github-ui/repos-list-blankslate'
import {useSearchParams} from '@github-ui/use-navigate'
import type {Icon} from '@primer/octicons-react'
import {RepoIcon, RepoLockedIcon} from '@primer/octicons-react'
import {Pagination} from '@primer/react'
import {useCallback, useEffect, useMemo, useRef, useState} from 'react'

import {useCurrentOrg} from '../contexts/CurrentOrgRepoContext'
import {useListResults} from '../hooks/use-list-results'
import {PropertiesEditingPage} from './PropertiesEditingPage'
import {DefinitionsPageHeader} from './PropertiesHeaderPageTabs'

// This page can only be accessed by org admins
const adminUserInfo: UserInfo = {
  admin: true,
  // This page never allows creating repos, no matter the current user
  canCreateRepository: false,
  directOrTeamMember: true,
}

const debounceAnnounce = debounce(announce, 300)

export function SetValuesPage() {
  const payload = useRoutePayload<OrgCustomPropertiesSetValuesPagePayload>()

  const {
    definitions,
    namespaces,
    propertiesLimits,
    pageCount,
    repositories,
    repositoryCount,
    permissions,
    ownDefinitionsCount,
  } = payload

  const [initialParams] = useSearchParams()
  const [editingRepos, setEditingRepos] = useState<Repository[]>([])
  const [query, setQuery] = useState(initialParams.get('q') || '')
  const org = useCurrentOrg()

  const {setBanner} = useBannerContext()

  const {results, currentPage, isFetching, fetchResults} = useListResults(
    {repositories, pageCount, repositoryCount},
    listOrgReposPropertyValuesPath({org: org.login}),
    Number(initialParams.get('page')),
  )

  const [selectedRepoIds, setSelectedRepoIds] = useState<Set<number>>(new Set())

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setSelectedRepoIds(new Set())
  }, [currentPage, query])

  const message = getSetValuesScreenReaderMessage(
    results.repositoryCount,
    currentPage,
    !!query,
    pageCount,
    adminUserInfo,
  )
  useEffect(() => {
    if (message) debounceAnnounce(message)
  }, [message])

  const editableProperties = useMemo(() => definitions.map(d => d.propertyName), [definitions])

  const onEditSuccess = () => {
    fetchResults({page: currentPage, q: query})
    setBanner('values.updated.success')
  }

  const selectRepos = useCallback(
    (repositorySelections: Repository[], buttonId: string) => {
      setBanner(null)
      setEditingRepos(repositorySelections)
      clickedEditButtonRef.current = {buttonId, offset: window.scrollY}
    },
    [setBanner, setEditingRepos],
  )

  const clickedEditButtonRef = useRef<null | {buttonId: string; offset: number}>(null)

  const isEditing = !!editingRepos.length
  useEffect(() => {
    const {buttonId, offset} = clickedEditButtonRef.current || {}
    if (!isEditing && buttonId) {
      const button = document.getElementById(buttonId)
      button?.focus()
      window.scrollTo({top: offset})
    }
  }, [isEditing])

  if (isEditing) {
    const closePage = () => setEditingRepos([])

    const pageTitle =
      editingRepos.length === 1 && editingRepos[0]
        ? `Set properties on ${editingRepos[0].name}`
        : `Set properties on ${editingRepos.length} selected repositories`

    return (
      <PropertiesEditingPage
        definitions={definitions}
        editableProperties={editableProperties}
        onClose={closePage}
        onSuccess={() => {
          onEditSuccess()
          closePage()
        }}
        propertiesLimits={propertiesLimits}
        editingTargets={editingRepos}
        renderPageHeader={({canClose}) => (
          <PropertiesBreadcrumbsPageHeader
            className="tmp-mb-3"
            pageTitle={pageTitle}
            breadcrumbLinkProps={{
              to: '#',
              onClick: async e => {
                e.preventDefault()
                if (await canClose()) closePage()
              },
            }}
          />
        )}
        setValuesPath={updateRepoPropertyValuesFromOrgPath({org: org.login})}
      />
    )
  }

  function updateQuery(q: string) {
    setQuery(q)
  }

  function submitQuery(q: string) {
    fetchResults({q, page: 1})
  }

  return (
    <>
      <DefinitionsPageHeader ownDefinitionsCount={ownDefinitionsCount} permissions={permissions} />
      {permissions === 'all' && (
        <PropertiesPageTabs initialActiveTab="set-values" definitionsCount={definitions.length} className="tmp-mb-3" />
      )}
      <ReposFilter
        id="repos-list-filter"
        className="flex-1 tmp-mb-3"
        label="Search repositories"
        variant="input"
        placeholder="Search repositories"
        filterValue={query}
        definitions={definitions}
        namespaces={namespaces}
        onChange={updateQuery}
        onSubmit={filterQuery => submitQuery(filterQuery.raw)}
      />
      <div {...hpcAttrs()}>
        <TargetsList
          title="Repositories"
          targetLiterals={{
            single: 'repository',
            multiple: 'repositories',
          }}
          selectedTargetIds={selectedRepoIds}
          onSelectionChange={setSelectedRepoIds}
          onEditTargetPropertiesClick={selectRepos}
          totalCount={results.repositoryCount}
          targets={results.repositories}
          isLoading={isFetching}
          renderEmptyState={() => (
            <NoReposMessage
              currentPage={currentPage}
              pageCount={results.pageCount}
              filtered={!!query}
              userInfo={adminUserInfo}
            />
          )}
          renderItem={(repo, itemProps) => (
            <TargetsList.Item
              key={repo.id}
              name={repo.name}
              description={repo.description}
              leadingIcon={visibilityMap[repo.visibility]}
              properties={repo.properties}
              linkToTarget={repoOverviewUrl({name: repo.name, ownerLogin: org.login})}
              {...itemProps}
            />
          )}
        />
        {results.repositories.length ? (
          <Pagination
            pageCount={results.pageCount}
            currentPage={currentPage}
            onPageChange={(e, page) => {
              // Avoid adding hash parameters
              e.preventDefault()

              window.scrollTo({top: 0, behavior: 'smooth'})
              fetchResults({page, q: query})
            }}
          />
        ) : null}
      </div>
    </>
  )
}

const visibilityMap: Record<Repository['visibility'], Icon> = {
  private: RepoLockedIcon,
  public: RepoIcon,
  internal: RepoIcon,
}

function getSetValuesScreenReaderMessage(
  resultsCount: number,
  currentPage: number,
  filtered: boolean,
  pageCount: number,
  userInfo: UserInfo | undefined,
) {
  if (resultsCount > 0) return ''
  const {heading, description} = getNoRepoMessage({currentPage, filtered, pageCount, userInfo})
  return heading && `${heading}${description && ` ${description}`}`
}
